<template>
  <div class="generate-all-entries">
    <button
      class="btn btn-primary"
      @click="showConfirmation"
      :disabled="generating"
    >
      <i v-if="generating" class="fas fa-spinner fa-spin me-2"></i>
      <i v-else class="fas fa-book me-2"></i>
      {{ $t('accounting.generateAllEntries') }}
    </button>

    <!-- Results Summary -->
    <div v-if="results" class="mt-3">
      <div class="alert alert-info">
        <h6 class="mb-2">{{ $t('accounting.generationResults') }}</h6>
        <ul class="mb-0 small">
          <li class="text-success">
            <i class="fas fa-check me-1"></i>
            {{ $t('accounting.generated') }}: {{ results.generated.length }}
          </li>
          <li class="text-warning">
            <i class="fas fa-forward me-1"></i>
            {{ $t('accounting.skipped') }}: {{ results.skipped.length }}
          </li>
          <li v-if="results.errors.length > 0" class="text-danger">
            <i class="fas fa-times me-1"></i>
            {{ $t('accounting.errors') }}: {{ results.errors.length }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";

export default {
  name: "GenerateAllEntriesButton",
  props: {
    operationType: {
      type: String,
      required: true,
      validator: (value) => ["shipping", "proform"].includes(value),
    },
    operationId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      generating: false,
      results: null,
    };
  },
  methods: {
    async showConfirmation() {
      const result = await Swal.fire({
        icon: "question",
        title: this.$tn("accounting.confirmGenerateAll"),
        text: this.$tn("accounting.confirmGenerateAllText"),
        showCancelButton: true,
        confirmButtonText: this.$tn("accounting.generate"),
        cancelButtonText: this.$tn("Cancel"),
        confirmButtonColor: "#0d6efd",
      });

      if (result.isConfirmed) {
        await this.generateAll();
      }
    },

    async generateAll() {
      try {
        this.generating = true;
        this.results = null;

        const response = await wencesApi.post(
          `/api/accounting/generate-all/${this.operationType}/${this.operationId}`
        );

        if (response.data.success) {
          this.results = response.data.results;

          const totalGenerated = response.data.results.generated.length;
          const totalSkipped = response.data.results.skipped.length;
          const totalErrors = response.data.results.errors.length;

          // Build skipped details
          let skippedHtml = '';
          if (totalSkipped > 0) {
            skippedHtml = `
              <div class="text-start mt-2">
                <strong class="text-warning"><i class="fas fa-forward me-1"></i> ${this.$t("accounting.skipped")} (${totalSkipped}):</strong>
                <ul class="small mb-0 mt-1" style="max-height: 150px; overflow-y: auto;">
                  ${response.data.results.skipped.map(s => 
                    `<li><code>${s.sourceModel}</code>: ${s.reason}</li>`
                  ).join('')}
                </ul>
              </div>
            `;
          }

          // Build errors details
          let errorsHtml = '';
          if (totalErrors > 0) {
            errorsHtml = `
              <div class="text-start mt-2">
                <strong class="text-danger"><i class="fas fa-times me-1"></i> ${this.$t("accounting.errors")} (${totalErrors}):</strong>
                <ul class="small mb-0 mt-1" style="max-height: 150px; overflow-y: auto;">
                  ${response.data.results.errors.map(e => 
                    `<li><code>${e.sourceModel}</code>: ${e.error}</li>`
                  ).join('')}
                </ul>
              </div>
            `;
          }

          if (totalGenerated > 0) {
            Swal.fire({
              icon: "success",
              title: this.$tn("accounting.entriesGenerated"),
              html: `
                <p><i class="fas fa-check text-success me-1"></i> ${this.$tn("accounting.generated")}: <strong>${totalGenerated}</strong></p>
                ${skippedHtml}
                ${errorsHtml}
              `,
              width: totalSkipped > 0 || totalErrors > 0 ? '600px' : undefined,
            });
          } else {
            Swal.fire({
              icon: "info",
              title: this.$tn("accounting.noNewEntries"),
              html: `
                <p>${this.$tn("accounting.allEntriesExist")}</p>
                ${skippedHtml}
                ${errorsHtml}
              `,
              width: totalSkipped > 0 || totalErrors > 0 ? '600px' : undefined,
            });
          }

          this.$emit("entries-generated", this.results);
        }
      } catch (error) {
        console.error("Error generating all entries:", error);

        Swal.fire({
          icon: "error",
          title: this.$tn("Error"),
          text: error.response?.data?.error || this.$tn("accounting.errorGenerating"),
        });
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style scoped>
.generate-all-entries {
  display: inline-block;
}
</style>

